import { api } from "@/lib/api";
import {
  tourAgenciesDtoListSchema,
  TourAgencyDomain,
  TourAgencyDto,
  TourAgencyDtoSchema,
  tourAgencyFromDto,
} from "@/models";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useToast } from "../use-toast";

const toastDomainErrorMessageMap: Record<TourAgencyDomain, string> = {
  agency: "toast.getTourAgencyTourError",
  tour: "toast.getTourAgencyAgencyError",
};
const toastDomainGetOneErrorMessageMap: Record<TourAgencyDomain, string> = {
  agency: "toast.getTourAgencyTourDetailError",
  tour: "toast.getTourAgencyAgencyDetailError",
};

export function getTourAgenciesQueryOptions(
  domain: TourAgencyDomain,
  domainId: string,
) {
  return queryOptions({
    queryKey: ["tour-agencies", domain, domainId],
    queryFn: async () => {
      const result: Array<TourAgencyDto> = [];
      let page = 0;
      let hasNextPage = true;
      while (hasNextPage) {
        page += 1;
        const res = await api.getWithCache(
          "/api/tour-agencies",
          tourAgenciesDtoListSchema,
          {
            query: {
              page: page.toString(),
            },
            filters: {
              AgencyId:
                domain === "agency" ? Number.parseInt(domainId) : undefined,
              TourId: domain === "tour" ? Number.parseInt(domainId) : undefined,
            },
            projection: ["Agency", "Tour.RegionNavigation"],
          },
        );
        result.push(...res.Data);
        hasNextPage = res.PageInfo.HasNextPage;
      }
      return result.map(tourAgencyFromDto);
    },
  });
}

export function useGetTourAgencies<TERROR extends Error>(
  domain: TourAgencyDomain,
  domainId: string,
  opts?: {
    onError: (error: TERROR) => void;
  },
) {
  const { toast } = useToast();

  const q = useQuery(getTourAgenciesQueryOptions(domain, domainId));
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: toastDomainErrorMessageMap[domain],
      variant: "destructive",
    });
    opts?.onError(q.error as TERROR);
  }, [domain, opts, q.error, toast]);
  return q;
}

export const getAllTourAgenciesQueryOptions = queryOptions({
  queryKey: ["all-tour-agencies"],
  queryFn: async () => {
    const result: Array<TourAgencyDto> = [];
    let page = 0;
    let hasNextPage = true;
    while (hasNextPage) {
      page += 1;
      const res = await api.getWithCache(
        "/api/tour-agencies",
        tourAgenciesDtoListSchema,
        {
          query: {
            page: page.toString(),
          },
          projection: ["Agency", "Tour.RegionNavigation"],
        },
      );
      result.push(...res.Data);
      hasNextPage = res.PageInfo.HasNextPage;
    }
    return result.map(tourAgencyFromDto);
  },
});

export function useGetAllTourAgencies() {
  return useQuery(getAllTourAgenciesQueryOptions);
}

export function getTourAgencyQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["tour-agencies-detail", id],
    queryFn: () =>
      api
        .get("/api/tour-agencies/$id", TourAgencyDtoSchema, {
          pathReplace: { $id: id },
          projection: ["Agency", "Tour"],
        })
        .then(tourAgencyFromDto),
  });
}

export function useGetTourAgency<TERROR extends Error>(
  domain: TourAgencyDomain,
  id: string,
  opts?: { onError?: (error: TERROR) => void; enabled?: boolean },
) {
  const { toast } = useToast();
  const q = useQuery({
    ...getTourAgencyQueryOptions(id),
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: toastDomainGetOneErrorMessageMap[domain],
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [domain, opts, q.error, toast]);
  return q;
}
